import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import api, { adminAPI } from '../services/api';
import { toast } from 'sonner';
import { Settings, Save, Loader2, Award, RotateCcw } from 'lucide-react';

const METADATA_LABELS = {
  ELIOS_BIRTHDAY_RECIPIENTS: 'Destinatários do lembrete de aniversário',
  BIRTHDAY_WHATSAPP_TEMPLATE_NAME: 'Template de aniversário aprovado na Meta',
  BIRTHDAY_WHATSAPP_TEMPLATE_NAME_WITH_PHOTO: 'Template de aniversário com foto aprovado na Meta',
  GAMIFICATION_MESSAGES_ENABLED: 'Envio do convite de registro de gamificação',
  MAGNUS_MESSAGES_ENABLED: 'Envio das dicas da Meta Magnus',
};

const METADATA_DESCRIPTIONS = {
  ELIOS_BIRTHDAY_RECIPIENTS:
    'Números de WhatsApp (com DDD, separados por vírgula) que recebem o aviso de aniversário. A WhatsApp Cloud API não envia mensagens para grupos, então o ELIOS manda individualmente para cada número aqui.',
  BIRTHDAY_WHATSAPP_TEMPLATE_NAME:
    'Nome do template de mensagem de aniversário aprovado no WhatsApp Manager da Meta. Enquanto estiver vazio, o envio fica desativado.',
  BIRTHDAY_WHATSAPP_TEMPLATE_NAME_WITH_PHOTO:
    'Nome do template com header de imagem, usado quando o aniversariante tem foto de perfil pública. Sem foto (ou com este campo vazio), o ELIOS usa o template de texto acima.',
  GAMIFICATION_MESSAGES_ENABLED:
    'Controla se o ELIOS envia o convite de registro de gamificação via WhatsApp (seg/qua/sex). Desative enquanto o template Utility ainda não foi aprovado na Meta.',
  MAGNUS_MESSAGES_ENABLED:
    'Controla se o ELIOS envia as dicas da Meta Magnus via WhatsApp (seg/qua/sex). Quando a gamificação também está ativa, as duas viram uma única mensagem. Desative enquanto o template Utility ainda não foi aprovado na Meta.',
};

const AdminSettingsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [gamificationPrompt, setGamificationPrompt] = useState('');
  const [isDefaultGamificationPrompt, setIsDefaultGamificationPrompt] = useState(true);
  const [gamificationPromptLoading, setGamificationPromptLoading] = useState(true);
  const [savingGamificationPrompt, setSavingGamificationPrompt] = useState(false);

  useEffect(() => {
    loadMetadata();
    loadGamificationPrompt();
  }, []);

  const loadGamificationPrompt = async () => {
    setGamificationPromptLoading(true);
    try {
      const response = await api.get('/admin/gamification/prompt');
      setGamificationPrompt(response.data.prompt);
      setIsDefaultGamificationPrompt(response.data.is_default);
    } catch (error) {
      toast.error('Erro ao carregar o prompt de avaliação de gamificação');
    } finally {
      setGamificationPromptLoading(false);
    }
  };

  const handleSaveGamificationPrompt = async () => {
    setSavingGamificationPrompt(true);
    try {
      await api.put('/admin/gamification/prompt', { prompt: gamificationPrompt });
      toast.success('Prompt de gamificação salvo com sucesso!');
      setIsDefaultGamificationPrompt(false);
    } catch (error) {
      toast.error('Erro ao salvar o prompt de gamificação');
    } finally {
      setSavingGamificationPrompt(false);
    }
  };

  const handleResetGamificationPrompt = async () => {
    try {
      const response = await api.post('/admin/gamification/prompt/reset', {});
      setGamificationPrompt(response.data.prompt);
      setIsDefaultGamificationPrompt(true);
      toast.success('Prompt de gamificação resetado para o padrão');
    } catch (error) {
      toast.error('Erro ao resetar o prompt de gamificação');
    }
  };

  const loadMetadata = async () => {
    try {
      const response = await adminAPI.getMetadata();
      setItems(response.data);
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (name, value) => {
    setItems((prev) => prev.map((item) => (
      item.name === name ? { ...item, value } : item
    )));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateMetadata(items.map(({ name, type, value }) => ({ name, type, value })));
      toast.success('Configurações salvas com sucesso');
      await loadMetadata();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-settings-page">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
            <Settings className="text-white" />
            Configurações
          </h1>
          <p className="text-neutral-400 mt-1">
            Gerencie variáveis não sensíveis usadas pela integração do ELIOS.
          </p>
        </div>

        <Card className="glass-card border-white/10">
          <CardHeader>
            <CardTitle className="text-lg text-white">Metadata da Integração WhatsApp</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="spinner" />
              </div>
            ) : (
              items.map((item) => (
                <div key={item.name} className="space-y-2">
                  <Label className="text-neutral-300">{METADATA_LABELS[item.name] || item.name}</Label>
                  {METADATA_DESCRIPTIONS[item.name] ? (
                    <p className="text-xs text-neutral-500">{METADATA_DESCRIPTIONS[item.name]}</p>
                  ) : null}
                  {item.type === 'boolean' ? (
                    <div className="flex items-center gap-3 pt-1">
                      <Switch
                        checked={item.value === 'true'}
                        onCheckedChange={(checked) => handleChange(item.name, checked ? 'true' : 'false')}
                        data-testid={`toggle-${item.name}`}
                      />
                      <span className="text-sm text-neutral-300">
                        {item.value === 'true' ? 'Ativado' : 'Desativado'}
                      </span>
                    </div>
                  ) : (
                    <Input
                      value={item.value || ''}
                      onChange={(event) => handleChange(item.name, event.target.value)}
                      placeholder={`Informe ${item.name}`}
                      className="bg-neutral-900/50 border-neutral-800 text-white"
                    />
                  )}
                </div>
              ))
            )}

            <div className="pt-4 border-t border-white/10">
              <Button
                onClick={handleSave}
                disabled={loading || saving}
                className="btn-primary w-full sm:w-auto"
                data-testid="save-settings-btn"
              >
                {saving ? (
                  <Loader2 className="mr-2 animate-spin" size={18} />
                ) : (
                  <Save className="mr-2" size={18} />
                )}
                Salvar configurações
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-white/10">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Award className="text-white" size={20} />
                Prompt de Avaliação de Gamificação
              </span>
              {!isDefaultGamificationPrompt && (
                <span className="text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-400">
                  Personalizado
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-neutral-400 text-sm">
              Prompt usado pela IA para decidir se um relato vale como Conquista (100 pts) ou Super Conquista
              (1000 pts) — ou se não é uma conquista pontuável. Ajuste aqui os critérios sem precisar de deploy.
            </p>

            {gamificationPromptLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="spinner" />
              </div>
            ) : (
              <Textarea
                value={gamificationPrompt}
                onChange={(e) => setGamificationPrompt(e.target.value)}
                className="bg-neutral-900/50 border-neutral-800 text-white font-mono text-sm min-h-[400px]"
                placeholder="Digite o prompt de avaliação de gamificação..."
                data-testid="gamification-prompt"
              />
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-white/10">
              <Button
                onClick={handleSaveGamificationPrompt}
                disabled={savingGamificationPrompt}
                className="btn-primary flex-1"
                data-testid="save-gamification-prompt-btn"
              >
                {savingGamificationPrompt ? (
                  <Loader2 className="mr-2 animate-spin" size={18} />
                ) : (
                  <Save className="mr-2" size={18} />
                )}
                Salvar Prompt
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="border-neutral-700 text-neutral-400 hover:text-white">
                    <RotateCcw className="mr-2" size={18} />
                    Resetar para Padrão
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="glass-card border-white/10">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Resetar Prompt?</AlertDialogTitle>
                    <AlertDialogDescription className="text-neutral-400">
                      Isso substituirá o prompt atual pelo padrão do sistema. Esta ação não pode ser desfeita.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-neutral-800 text-white border-neutral-700">
                      Cancelar
                    </AlertDialogCancel>
                    <AlertDialogAction onClick={handleResetGamificationPrompt} className="bg-amber-600 hover:bg-amber-700">
                      Resetar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminSettingsPage;
